// Visual indicators for "this team member is currently on leave —
// here's who's covering and until when." Two flavours:
//
//   1. <AwayBanner>   — a full-width tinted card for the ticket-detail
//                       header. Big enough to read at a glance, mirrors
//                       the existing "Snoozed until …" / "Merged into
//                       …" banner aesthetic so the visual vocabulary is
//                       consistent.
//   2. <AwayDot>      — a small amber pip overlaid on the assignee
//                       avatar in TicketRow. Carries the same info via
//                       `title` so hover reveals the full string
//                       without taking up row space.
//
// Both source their data from `useActiveAwayPeriods` (one team-wide
// query, polled every 5 min). The team-members map gets passed through
// so display names + backup avatar work.

import { Plane } from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import type { AwayPeriod, TeamMember } from '@/lib/queries';

/** "Fri, 23 May 5:00 PM" — short, human-readable, with weekday so the
 *  reader can mentally place "is that this Friday or next?". Matches
 *  the format style used by the snooze banner. */
export function formatAwayUntil(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Single-line summary string used by tooltips + screen-reader labels.
 *  e.g. "Rex is on leave · Liz covering until Fri, 23 May 5:00 PM". */
export function buildAwayLabel(opts: {
  awayName: string;
  backupName: string;
  endsAt: string;
}): string {
  return `${opts.awayName} is on leave · ${opts.backupName} covering until ${formatAwayUntil(opts.endsAt)}`;
}

interface BannerProps {
  period: AwayPeriod;
  /** Display name of the away person (the ticket assignee). */
  awayName: string;
  /** Display name of the backup. */
  backupName: string;
  teamMembersBySlackId?: Map<string, TeamMember>;
}

export function AwayBanner({
  period,
  awayName,
  backupName,
  teamMembersBySlackId,
}: BannerProps) {
  return (
    <div className="mb-3 flex items-start gap-3 rounded-md border border-tint-warning-ring bg-tint-warning px-3 py-2.5 text-sm text-tint-warning-fg">
      <Plane className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          <span>{awayName} is on leave</span>
          <span className="text-tint-warning-fg/70"> until </span>
          <span>{formatAwayUntil(period.ends_at)}</span>
        </p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-tint-warning-fg/80">
          <span>Covered by</span>
          <UserAvatar
            slackId={period.backup_slack_id}
            teamMembersBySlackId={teamMembersBySlackId}
            size="xs"
          />
          <span className="font-medium text-tint-warning-fg">{backupName}</span>
          {period.reason ? (
            <>
              <span aria-hidden>·</span>
              <span className="italic">{period.reason}</span>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}

interface DotProps {
  /** The full human-readable label, e.g.
   *  "Rex is on leave · Liz covering until Fri, 23 May 5:00 PM".
   *  Surfaced via the `title` attribute so hover/keyboard focus reveals
   *  the context without crowding the row. */
  label: string;
}

/** A small amber pip you overlay on the bottom-right of an assignee
 *  avatar to signal "this person is currently away". Designed to stack
 *  with the existing presence dot (different colour + slightly larger
 *  ring) so both signals can coexist if needed. */
export function AwayDot({ label }: DotProps) {
  return (
    <span
      title={label}
      aria-label={label}
      className="absolute -bottom-0.5 -right-0.5 inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-tint-warning-fg ring-2 ring-background"
    >
      <span className="sr-only">on leave</span>
    </span>
  );
}
